import { useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  IconBuilding,
  IconCard,
  IconClock,
  IconCode,
  IconDocs,
  IconNota,
  IconOverview,
  IconTeam,
  Pill,
} from '@fa/ui';
import { api, getToken, setToken } from './lib/api.js';
import type { ContingencyItem, SessionView } from './lib/types.js';
import { Login } from './Login.js';
import { Overview } from './pages/Overview.js';
import { Documents } from './pages/Documents.js';
import { Contingency } from './pages/Contingency.js';
import { Emitters } from './pages/Emitters.js';
import { Integration } from './pages/Integration.js';
import { Team } from './pages/Team.js';
import { Billing } from './pages/Billing.js';

type View = 'overview' | 'docs' | 'cont' | 'emit' | 'integ' | 'team' | 'bill';

const NAV: { id: View; label: string; icon: () => ReactNode; group?: string }[] = [
  { id: 'overview', label: 'Visão geral', icon: IconOverview },
  { id: 'docs', label: 'Notas', icon: IconDocs },
  { id: 'cont', label: 'Contingência', icon: IconClock },
  { id: 'emit', label: 'Empresas', icon: IconBuilding, group: 'Configuração' },
  { id: 'integ', label: 'Integração', icon: IconCode },
  { id: 'team', label: 'Equipe', icon: IconTeam },
  { id: 'bill', label: 'Plano e faturas', icon: IconCard },
];

export function App(): ReactNode {
  const [signedIn, setSignedIn] = useState(() => getToken() !== null);
  const [view, setView] = useState<View>('overview');

  if (!signedIn) return <Login onSignedIn={() => setSignedIn(true)} />;

  return (
    <Shell
      view={view}
      onView={setView}
      onSignOut={() => {
        setToken(null);
        setSignedIn(false);
      }}
    />
  );
}

function Shell({
  view,
  onView,
  onSignOut,
}: {
  view: View;
  onView: (v: View) => void;
  onSignOut: () => void;
}): ReactNode {
  const session = useQuery({
    queryKey: ['session'],
    queryFn: () => api<SessionView>('/v1/auth/me'),
    staleTime: 5 * 60_000,
  });

  // O contador de contingencia fica na navegacao: e a informacao que nao pode
  // esperar o usuario lembrar de conferir.
  const contingency = useQuery({
    queryKey: ['contingency'],
    queryFn: () => api<{ data: ContingencyItem[] }>('/v1/portal/contingency'),
    refetchInterval: 60_000,
  });

  const pendentes = contingency.data?.data.length ?? 0;
  const tenant = session.data?.tenant;
  const user = session.data?.user;
  const initials = (user?.name ?? 'FA')
    .split(' ')
    .map((p) => p[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <div className="app">
      <aside className="side">
        <div className="logo">
          <i>
            <IconNota />
          </i>{' '}
          FA Tech Notas
        </div>
        <nav aria-label="Principal">
          {NAV.map((item) => (
            <div key={item.id}>
              {item.group && <div className="group">{item.group}</div>}
              <button
                type="button"
                onClick={() => {
                  onView(item.id);
                  window.scrollTo(0, 0);
                }}
                {...(view === item.id ? { 'aria-current': 'page' as const } : {})}
              >
                <item.icon />
                {item.label}
                {item.id === 'cont' && pendentes > 0 && <span className="badge">{pendentes}</span>}
              </button>
            </div>
          ))}
        </nav>
        <div className="foot">
          <b>{tenant?.name ?? '—'}</b>
          <span>
            {tenant ? `Plano ${tenant.plan}` : 'Carregando...'}
            {tenant?.blocked ? ' · emissão bloqueada' : ''}
          </span>
          <button
            className="btn btn-sm btn-ghost"
            style={{ marginTop: 10, width: '100%', justifyContent: 'center' }}
            onClick={onSignOut}
          >
            Sair
          </button>
        </div>
      </aside>

      <div className="main">
        <TopBar initials={initials} tenantName={tenant?.name ?? ''} />
        <div className="content">
          {view === 'overview' && <Overview onOpenDocuments={() => onView('docs')} />}
          {view === 'docs' && <Documents />}
          {view === 'cont' && <Contingency />}
          {view === 'emit' && <Emitters />}
          {view === 'integ' && <Integration />}
          {view === 'team' && <Team />}
          {view === 'bill' && <Billing />}
        </div>
      </div>
    </div>
  );
}

/**
 * Barra superior com o estado da SEFAZ.
 *
 * Fica sempre visivel porque muda a leitura de tudo: uma nota "na fila" as
 * 14h com a SEFAZ PR fora do ar nao e um problema do sistema.
 */
function TopBar({ initials, tenantName }: { initials: string; tenantName: string }): ReactNode {
  const emitters = useQuery({
    queryKey: ['emitters'],
    queryFn: () => api<{ data: { uf: string; cnpj: string; razao_social: string }[] }>('/v1/emitters'),
    staleTime: 5 * 60_000,
  });

  const ufs = [...new Set((emitters.data?.data ?? []).map((e) => e.uf))];

  return (
    <div className="top">
      <div className="select">
        {tenantName || 'Carregando'}{' '}
        <small>{emitters.data?.data[0]?.cnpj ? formatCnpj(emitters.data.data[0].cnpj) : ''}</small>
      </div>
      <div className="env">
        <span className="on">Produção</span>
        <span>Sandbox</span>
      </div>
      <div className="sefaz">
        SEFAZ agora:
        {ufs.map((uf) => (
          <SefazPill key={uf} uf={uf} />
        ))}
      </div>
      <div className="avatar">{initials}</div>
    </div>
  );
}

function SefazPill({ uf }: { uf: string }): ReactNode {
  const status = useQuery({
    queryKey: ['sefaz', uf],
    queryFn: () =>
      // Modelo explicito: a rota tem um padrao, mas depender dele aqui faz a
      // pastilha falar de um servico sem dizer qual. O detalhe por modelo
      // esta no painel do dashboard, que consulta os dois.
      api<{ online: boolean | null; verified: boolean; circuit: string }>(
        `/v1/sefaz/status?uf=${uf}&environment=sandbox&model=55`,
      ),
    refetchInterval: 60_000,
    retry: false,
  });

  if (status.isLoading) return <span className="pill mute">{uf}</span>;
  if (status.isError) return <span className="pill mute">{uf} ?</span>;

  // `online` vem nulo quando nao foi possivel consultar. Tratar isso como
  // "fora" dizia ao lojista que a SEFAZ caiu por causa de um problema nosso —
  // e lojista que ve "SEFAZ fora" para de emitir.
  const online = status.data?.online ?? null;
  if (online === null) return <span className="pill mute">{uf}</span>;

  const lenta = online && status.data?.circuit === 'open';
  return (
    <Pill tone={!online ? 'bad' : lenta ? 'warn' : 'ok'}>
      {!online ? `${uf} fora` : lenta ? `${uf} lenta` : uf}
    </Pill>
  );
}

const formatCnpj = (v: string): string => {
  const d = v.replace(/\D/g, '');
  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
};
